import { use, useSyncExternalStore, useCallback } from 'react';
import { useModals, PlumaModalManagerContext } from '@customerio/pluma-components/react';
import { useCopilotSessionContext, useCopilotSidebar, useMasqAccessLevel } from '@/features/copilot';
import { CommandDialog } from './command-dialog';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
// Persists the last typed query across open/close cycles so reopening restores it.
// Scoped to a workspace ID — stale queries from a previous workspace are discarded.
let _lastInputValue = '';
let _lastInputWorkspaceId: number | undefined;
let _openTimeMs: number | null = null;
let _lastActiveFilter: string = 'all';
let _lastActiveSubFilter: string | null = null;
let _lastIsFiltersVisible = true;
let _lastIsQuickLookOpen = true;

const persistInputValue = (value: string) => {
  _lastInputValue = value;
};

const persistActiveFilter = (value: string) => {
  _lastActiveFilter = value;
};

const persistActiveSubFilter = (value: string | null) => {
  _lastActiveSubFilter = value;
};

const persistFiltersVisibility = (value: boolean) => {
  _lastIsFiltersVisible = value;
};

const persistQuickLookOpen = (value: boolean) => {
  _lastIsQuickLookOpen = value;
};

function getLastInputValue(workspaceId: number): string {
  if (_lastInputWorkspaceId !== workspaceId) {
    _lastInputValue = '';
    _lastActiveFilter = 'all';
    _lastActiveSubFilter = null;
    _lastIsFiltersVisible = true;
    _lastIsQuickLookOpen = true;
    _lastInputWorkspaceId = workspaceId;
  }
  return _lastInputValue;
}
function getCloseTrackingData(workspaceId: number) {
  const hadQuery = getLastInputValue(workspaceId).trim().length > 0;
  const timeOpenMs = _openTimeMs == null ? 0 : Date.now() - _openTimeMs;

  return {
    hadQuery,
    query: getLastInputValue(workspaceId).trim(),
    timeOpenMs,
    activeFilter: _lastActiveFilter,
    activeSubFilter: _lastActiveSubFilter,
  };
}

const noopSubscribe = () => () => {};
const emptySnapshot = () => [];

/**
 * CommandDialog is mounted by Pluma's managed-modal layer (alongside the main app shell, not
 * inside the authenticated layout outlet). Providers that only wrap authenticated content —
 * e.g. CurrentWorkspaceProvider, CopilotSessionProvider, AuthProvider — are therefore not
 * ancestors of the modal.
 *
 * Anything that depends on that layout-scoped context must be captured in this hook (which runs
 * under the top nav / authenticated tree) and passed through CommandDialogData. Current examples:
 *   - session, openCopilot  (CopilotSessionProvider / CopilotSidebarProvider)
 *   - workspace             (CurrentWorkspaceProvider)
 *   - track                 (useAnalytics reads workspace from context; no-ops without it)
 *   - isMasqWithoutAccess   (AuthProvider → useAuth → useMasqAccessLevel)
 *
 * Hooks that use Nanostores, the root QueryClient, React Router, SafeNavigateProvider, or
 * ActiveAppContextProvider still work inside CommandDialog without being passed in — e.g.
 * useFeatureFlag, useServicesQuery, useSafeNavigate, useMatches, useActiveBundle.
 */
export function useCommandDialog() {
  const modalApi = use(PlumaModalManagerContext);
  const modals = useSyncExternalStore(modalApi?.subscribe ?? noopSubscribe, modalApi?.getSnapshot ?? emptySnapshot);
  const isOpen = modals.some((m) => m.modal === CommandDialog && m.state.isOpen === true);

  const modalsApi = useModals();
  const session = useCopilotSessionContext();
  const workspace = useCurrentWorkspace();
  const { track } = useAnalytics();
  const { openCopilot } = useCopilotSidebar();
  const { isMasqWithoutAccess } = useMasqAccessLevel();
  const { account } = useAuth();
  const isBuilder = isBuilderPlan(account);

  const open = useCallback(
    (options?: { initialInputValue?: string }) => {
      const workspaceId = workspace?.id ?? 0;

      // Always call getLastInputValue first so the workspace-change guard fires
      // (resetting _lastActiveFilter to 'all' and updating _lastInputWorkspaceId)
      // before we read _lastActiveFilter or apply any override from options.
      // Do NOT short-circuit with ??, even when initialInputValue is provided.
      const persistedInputValue = getLastInputValue(workspaceId);
      const initialInputValue = options?.initialInputValue ?? persistedInputValue;

      _lastInputValue = initialInputValue;
      _openTimeMs = Date.now();

      modalsApi.open(CommandDialog, {
        session,
        workspace,
        initialInputValue,
        initialActiveFilter: _lastActiveFilter,
        initialActiveSubFilter: _lastActiveSubFilter,
        initialIsFiltersVisible: _lastIsFiltersVisible,
        initialIsQuickLookOpen: _lastIsQuickLookOpen,
        persistInputValue,
        persistActiveFilter,
        persistActiveSubFilter,
        persistFiltersVisibility,
        persistQuickLookOpen,
        track,
        openCopilot,
        isMasqWithoutAccess,
        isBuilder,
      });
    },
    [modalsApi, session, workspace, track, openCopilot, isMasqWithoutAccess, isBuilder],
  );

  const close = useCallback(() => {
    modalsApi.closeAllOfType(CommandDialog);
  }, [modalsApi]);

  const workspaceId = workspace?.id ?? 0;
  const getCloseTrackingSnapshot = useCallback(() => getCloseTrackingData(workspaceId), [workspaceId]);

  return { isOpen, open, close, getCloseTrackingSnapshot };
}
